import React, { useEffect, useRef, useState } from 'react';
import { collectFingerprint } from '../lib/deviceFingerprint';
import { deviceTrustAPI } from '../services/api';
import { useAuth } from '../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ShieldAlert, ShieldX, Clock, Loader2, LogOut } from 'lucide-react';

/**
 * Runs once per session after a user is authenticated.
 * - Registers the browser fingerprint (idempotent)
 * - Verifies against the server-side policy
 * - Blocks the UI with a modal if the device is blocked / not-registered under strict mode
 * If enforcement is off the component is invisible.
 */
const DeviceVerifier = () => {
  const { user, token, logout } = useAuth();
  const [gate, setGate] = useState(null); // {status, message}
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (!user || !token) { setGate(null); ran.current = false; return; }
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        setBusy(true);
        const attrs = await collectFingerprint();
        // Idempotent register (also captures context for IT)
        try {
          await deviceTrustAPI.register({
            fingerprint: attrs.fingerprint,
            user_agent: attrs.user_agent,
            platform: attrs.platform,
            screen: attrs.screen,
            timezone: attrs.timezone,
            language: attrs.language,
            hardware_concurrency: attrs.hardware_concurrency,
            device_memory: attrs.device_memory,
            color_depth: attrs.color_depth,
          });
        } catch (e) {
          // 400 if limit hit — still verify to show the reason
          const detail = e.response?.data?.detail;
          if (detail) toast.error(detail);
        }
        const v = await deviceTrustAPI.verify({ fingerprint: attrs.fingerprint });
        if (!v.data.enforcement) { setGate(null); return; }
        if (v.data.trusted) { setGate(null); return; }
        setGate(v.data);
      } catch (e) {
        // If the API errors, do NOT hard-lock the user — enforcement should never break login
        console.warn('[device-trust] verify failed', e);
        setGate(null);
      } finally {
        setBusy(false);
      }
    })();
  }, [user, token]);

  if (!gate) return null;

  const status = gate.status || 'unknown';
  const isBlocked = status === 'blocked' || status === 'revoked';
  const title = isBlocked
    ? 'Device blocked'
    : status === 'reapproval_required'
      ? 'Re-approval required'
      : 'Device awaiting approval';
  const Icon = isBlocked ? ShieldX : status === 'reapproval_required' ? Clock : ShieldAlert;

  const forceLogout = () => { setBusy(true); setTimeout(() => logout(), 300); };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="device-verifier-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon size={20} />{title}</DialogTitle>
          <DialogDescription>
            {gate.message || 'This device does not have access to Pride Global HRMS. Please contact IT.'}
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground space-y-1">
          <div>Status: <span className="font-mono">{status}</span></div>
          {gate.device_id && <div>Device ID: <span className="font-mono">{gate.device_id}</span></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={forceLogout} disabled={busy} data-testid="device-verifier-logout-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <LogOut size={14} className="mr-2" />}Sign out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceVerifier;
